<template>
  <div class="custom-tooltip">
  <span
    ref="triggerRef"
    class="pro-tooltip-trigger"
    :aria-describedby="open ? tooltipId : undefined"
    @mouseenter="onEnter"
    @mouseleave="onLeave"
    @focus="onEnter"
    @blur="onLeave"
    @click="onClick"
    @keydown.esc.stop.prevent="onEsc"
  >
  <slot/>
  </span>

    <transition name="pro-tooltip-fade">
      <div
        v-if="open && content"
        ref="tooltipRef"
        class="pro-tooltip"
        :id="tooltipId"
        role="tooltip"
        :data-theme="theme"
        :data-dir="direction"
        :style="tooltipStyle"
        @mouseenter="interactive ? (hoveringTooltip = true) : null"
        @mouseleave="interactive ? (hoveringTooltip = false, scheduleClose()) : null"
      >
        <div class="pro-tooltip-content" :style="contentStyle">
          <slot name="tooltip">{{ content }}</slot>
        </div>
        <div v-if="arrow" class="pro-tooltip-arrow" :style="arrowStyle"></div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'customTooltip',
  props: {
    content: {type: String, default: ''},
    placement: {type: String, default: 'top'},
    offset: {type: Number, default: 8},
    openDelay: {type: Number, default: 80},
    closeDelay: {type: Number, default: 80},
    interactive: {type: Boolean, default: true},
    value: {type: Boolean, default: false}, // v-model
    trigger: {type: String, default: 'hover'},
    arrow: {type: Boolean, default: true},
    maxWidth: {type: String, default: '280px'},
    theme: {type: String, default: 'dark'},
    direction: {type: String, default: 'ltr'}, // uc0u1580 u1583 u1740 u1583 : rtl u1740 u1575  ltr
    closeOnOutsideClick: {type: Boolean, default: true},
    zIndex: {type: Number, default: 999999}
  },
  data () {
    return {
      open: false,
      hoveringTooltip: false,
      tooltipId: `pt-${Math.random().toString(36).slice(2)}`,
      state: {x: 0, y: 0, placement: this.placement},
      openTimer: null,
      closeTimer: null
    }
  },
  computed: {
    tooltipStyle () {
      return {position: 'fixed', left: `${this.state.x}px`, top: `${this.state.y}px`, zIndex: this.zIndex}
    },
    contentStyle () {
      return {maxWidth: this.maxWidth}
    },
    arrowStyle () {
      const size = 8
      const style = {width: `${size * 2}px`, height: `${size * 2}px`}
      const p = this.state.placement
      if (p.startsWith('top')) {
        style.bottom = `-${size}px`
        style.left = '50%'
        style.transform = 'translateX(-50%) rotate(45deg)'
      } else if (p.startsWith('bottom')) {
        style.top = `-${size}px`
        style.left = '50%'
        style.transform = 'translateX(-50%) rotate(45deg)'
      } else if (p.startsWith('left')) {
        style.right = `-${size}px`
        style.top = '50%'
        style.transform = 'translateY(-50%) rotate(45deg)'
      } else {
        style.left = `-${size}px`
        style.top = '50%'
        style.transform = 'translateY(-50%) rotate(45deg)'
      }
      return style
    }
  },
  watch: {
    value (v) {
      if (this.trigger === 'manual') {
        v ? this.openTooltip() : this.closeTooltip()
      }
    }
  },
  methods: {
    onEnter () {
      if (this.trigger === 'hover' || this.trigger === 'focus') this.scheduleOpen()
    },
    onLeave () {
      if (this.trigger === 'hover' || this.trigger === 'focus') this.scheduleClose()
    },
    onClick () {
      if (this.trigger === 'click') (this.open ? this.closeTooltip() : this.openTooltip())
    },
    onEsc () {
      if (this.open) this.closeTooltip()
    },

    clearTimers () {
      if (this.openTimer) clearTimeout(this.openTimer)
      if (this.closeTimer) clearTimeout(this.closeTimer)
    },
    scheduleOpen () {
      this.clearTimers()
      this.openTimer = setTimeout(() => this.openTooltip(), this.openDelay)
    },
    scheduleClose () {
      this.clearTimers()
      this.closeTimer = setTimeout(() => {
        if (this.interactive && this.hoveringTooltip) return
        this.closeTooltip()
      }, this.closeDelay)
    },

    openTooltip () {
      if (this.open) return
      this.open = true
      this.$emit('input', true)
      this.$emit('show')
      this.$nextTick(() => {
        this.computePosition()
        this.bindGlobal()
      })
    },
    closeTooltip () {
      if (!this.open) return
      this.open = false
      this.$emit('input', false)
      this.$emit('hide')
      this.unbindGlobal()
    },
    bindGlobal () {
      window.addEventListener('scroll', this.onScroll, true)
      window.addEventListener('resize', this.onResize)
      if (this.closeOnOutsideClick) document.addEventListener('pointerdown', this.onDocPointerDown, true)
    },
    unbindGlobal () {
      window.removeEventListener('scroll', this.onScroll, true)
      window.removeEventListener('resize', this.onResize)
      document.removeEventListener('pointerdown', this.onDocPointerDown, true)
    },
    onScroll () {
      this.computePosition()
    },
    onResize () {
      this.computePosition()
    },
    onDocPointerDown (e) {
      const t = e.target
      if (this.$refs.triggerRef && this.$refs.triggerRef.contains(t)) return
      if (this.$refs.tooltipRef && this.$refs.tooltipRef.contains(t)) return
      this.closeTooltip()
    },
    computePosition () {
      const trigger = this.$refs.triggerRef, tooltip = this.$refs.tooltipRef
      if (!trigger || !tooltip) return
      const rect = trigger.getBoundingClientRect()
      const tipRect = tooltip.getBoundingClientRect()

      let placement = this.placement
      const gap = this.offset

      const fitsTop = rect.top - tipRect.height - gap > 0
      const fitsBottom = rect.bottom + tipRect.height + gap < window.innerHeight
      const fitsLeft = rect.left - tipRect.width - gap > 0
      const fitsRight = rect.right + tipRect.width + gap < window.innerWidth

      // Flip logic
      if (placement.startsWith('top') && !fitsTop && fitsBottom) placement = 'bottom'
      if (placement.startsWith('bottom') && !fitsBottom && fitsTop) placement = 'top'
      if (placement.startsWith('left') && !fitsLeft && fitsRight) placement = 'right'
      if (placement.startsWith('right') && !fitsRight && fitsLeft) placement = 'left'

      // Positioning
      let x = 0, y = 0
      if (placement === 'top') {
        y = rect.top - tipRect.height - gap
        x = rect.left + rect.width / 2 - tipRect.width / 2
      }
      if (placement === 'bottom') {
        y = rect.bottom + gap
        x = rect.left + rect.width / 2 - tipRect.width / 2
      }
      if (placement === 'left') {
        x = rect.left - tipRect.width - gap
        y = rect.top + rect.height / 2 - tipRect.height / 2
      }
      if (placement === 'right') {
        x = rect.right + gap
        y = rect.top + rect.height / 2 - tipRect.height / 2
      }

      // RTL adjustment (swap left/right)
      if (this.direction === 'rtl') {
        if (placement === 'left') placement = 'right'
        else if (placement === 'right') placement = 'left'
      }

      // Shift inside viewport
      const pad = 4
      x = Math.max(pad, Math.min(x, window.innerWidth - tipRect.width - pad))
      y = Math.max(pad, Math.min(y, window.innerHeight - tipRect.height - pad))

      this.state.x = Math.round(x)
      this.state.y = Math.round(y)
      this.state.placement = placement
    }
  },
  mounted () {
    if (this.trigger === 'manual' && this.value) this.openTooltip()
  },
  beforeDestroy () {
    this.clearTimers()
    this.unbindGlobal()
  }
}
</script>

<style scoped>
:root {
  --tooltip-bg: rgba(28, 28, 30, 0.98);
  --tooltip-color: #fff;
  --tooltip-border: rgba(255, 255, 255, 0.08);
}

.pro-tooltip-trigger {
  display: inline-flex;
  align-items: center;
}

.pro-tooltip {
  position: fixed;
  pointer-events: auto;
  will-change: transform;
}

.pro-tooltip .pro-tooltip-content {
  background: var(--tooltip-bg);
  color: var(--tooltip-color);
  border: 1px solid var(--tooltip-border);
  font-size: 12px;
  line-height: 1.2;
  padding: 8px 10px;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
}

.pro-tooltip[data-theme="light"] .pro-tooltip-content {
  --tooltip-bg: #fff;
  --tooltip-color: #111827;
  --tooltip-border: rgba(17, 24, 39, 0.12);
}

.pro-tooltip-arrow {
  position: absolute;
  background: inherit;
  border: inherit;
}

.pro-tooltip-fade-enter-active, .pro-tooltip-fade-leave-active {
  transition: opacity .12s ease, transform .12s ease;
}

.pro-tooltip-fade-enter, .pro-tooltip-fade-leave-to {
  opacity: 0;
  transform: scale(0.98);
}

</style>
